import React from 'react';
import round from 'utils/round';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';
import RepActivityItem from './RepActivityItem';
import RepProfileSectionHeader from './RepProfileSectionHeader';
import RepProfilePageActions from 'actions/RepProfilePageActions';
import Theme from 'styles/theme';
import './RepProfileActivities.styl';
import formatSecondsShort from 'utils/formatSecondsShort';

export default class RepProfileCoachingReceivedActivities extends React.PureComponent {
  render() {
    const {
      coachingActivity,
      coachingTeamMetrics,
      rep,
      isCoachingActivityLoading,
      isCoachingTeamMetricsLoading,
      periodText,
      startDate,
      endDate,
      headerStartDate,
      headerEndDate,
      coachingReceivedFrom,
      renderTeamAverage,
    } = this.props;
    const isFromManager = coachingReceivedFrom === 'manager';
    const listener = isFromManager
      ? 'their manager'
      : 'others in the team (aside from the manager)';
    const userIsManager = rep && rep.role === 'manager';
    const dataGetterForType = type => () =>
      RepProfilePageActions.getTrendData({
        userId: rep.id,
        direction: coachingReceivedFrom,
        type,
        startDate,
        endDate,
      });

    const color = isFromManager ? Theme.palette.outrageousOrange : Theme.palette.smoky;

    return coachingActivity || isCoachingActivityLoading ? (
      <section className="rep-profile-activities main-section">
        {(isCoachingActivityLoading || isCoachingTeamMetricsLoading) && <LoadingOverlay />}
        {coachingActivity && (
          <section className="stats">
            <RepProfileSectionHeader
              showTeamIcon={renderTeamAverage}
              title={
                isFromManager
                  ? 'Coaching Received' +
                    (rep ? ` by ${rep.firstName} ` : '') +
                    '(from current manager)'
                  : !userIsManager
                  ? 'Coaching Received From Others'
                  : 'Feedback Received'
              }
              startDate={headerStartDate}
              endDate={headerEndDate}
              location={this.props.location}
            />
            <div className="secondary-section-header">Activities</div>
            <div className="sub-section">
              <RepActivityItem
                startDate={headerStartDate}
                endDate={headerEndDate}
                rep={rep}
                repValue={coachingActivity.callsListenedTo}
                teamAverage={renderTeamAverage && coachingTeamMetrics.callsListenedToAvg}
                max={coachingTeamMetrics.callsListenedToMax}
                title="Calls Listened To"
                color={color}
                fetchTrendData={dataGetterForType('listening_count')}
                helpText={() =>
                  `The total number of the rep's calls that ${listener} listened to during the ${periodText} period of the report. Listening to any portion of a call counts towards this total; however, multiple listens to a call on the same day do not add to the total.`
                }
              />
              <RepActivityItem
                startDate={headerStartDate}
                endDate={headerEndDate}
                percentageBased
                rep={rep}
                repValue={coachingActivity.avgEachCallListenedTo}
                teamAverage={renderTeamAverage && coachingTeamMetrics.avgEachCallListenedToAvg}
                max={coachingTeamMetrics.avgEachCallListenedToMax}
                title="Average of Each Call Listened To"
                formatValue={value => round(value * 100) + '%'}
                color={color}
                fetchTrendData={dataGetterForType('listening_pct')}
                helpText={() =>
                  `The average percentage of the rep's calls that ${listener} listened to during the ${periodText} period of the report. Listening to the same portion of a call repeatedly does not add to the total.`
                }
              />
              <RepActivityItem
                startDate={headerStartDate}
                endDate={headerEndDate}
                rep={rep}
                repValue={coachingActivity.totalTimeListened}
                teamAverage={renderTeamAverage && coachingTeamMetrics.totalTimeListenedAvg}
                max={coachingTeamMetrics.totalTimeListenedMax}
                title="Total Time Listened"
                formatValue={value => formatSecondsShort(value, true, false, true)}
                color={color}
                fetchTrendData={dataGetterForType('listening_time')}
                helpText={() =>
                  `The total call time that ${listener} listened to during the ${periodText} period of the report.`
                }
              />
              <RepActivityItem
                startDate={headerStartDate}
                endDate={headerEndDate}
                rep={rep}
                repValue={coachingActivity.commentsCreated}
                teamAverage={renderTeamAverage && coachingTeamMetrics.commentsCreatedAvg}
                max={coachingTeamMetrics.commentsCreatedMax}
                title="Comments Created"
                color={color}
                fetchTrendData={dataGetterForType('comments_count')}
                helpText={() =>
                  `The total number of comments that ${listener} left on the rep's calls during the ${periodText} period of the report.`
                }
              />
              <RepActivityItem
                startDate={headerStartDate}
                endDate={headerEndDate}
                rep={rep}
                repValue={coachingActivity.commentLength}
                teamAverage={renderTeamAverage && coachingTeamMetrics.commentLengthAvg}
                max={coachingTeamMetrics.commentLengthMax}
                title="Total Comment Length"
                color={color}
                fetchTrendData={dataGetterForType('comments_length')}
                helpText={() =>
                  `The total number of characters in all comments that ${listener} left on the rep's calls during the ${periodText} period of the report.`
                }
              />
              <RepActivityItem
                startDate={headerStartDate}
                endDate={headerEndDate}
                rep={rep}
                repValue={coachingActivity.highlightsCreated}
                teamAverage={renderTeamAverage && coachingTeamMetrics.highlightsCreatedAvg}
                max={coachingTeamMetrics.highlightsCreatedMax}
                title="Highlights Created"
                color={color}
                fetchTrendData={dataGetterForType('highlights_count')}
                helpText={() =>
                  `The total number of highlights that ${listener} left on the rep's calls during the ${periodText} period of the report.`
                }
              />
              <RepActivityItem
                startDate={headerStartDate}
                endDate={headerEndDate}
                rep={rep}
                repValue={coachingActivity.callsAddedToLibraries}
                teamAverage={renderTeamAverage && coachingTeamMetrics.callsAddedToLibrariesAvg}
                max={coachingTeamMetrics.callsAddedToLibrariesMax}
                title="Calls Added To Shared Libraries"
                color={color}
                fetchTrendData={dataGetterForType('library_adds')}
                helpText={() =>
                  `The total number of the rep's calls that ${listener} added to shared libraries during the ${periodText} period of the report. Calls added to individual libraries do not count towards this total.`
                }
              />
              <RepActivityItem
                startDate={headerStartDate}
                endDate={headerEndDate}
                rep={rep}
                repValue={coachingActivity.scoreCardsCompleted}
                teamAverage={renderTeamAverage && coachingTeamMetrics.scoreCardsCompletedAvg}
                max={coachingTeamMetrics.scoreCardsCompletedMax}
                title="Scorecards Completed"
                color={color}
                fetchTrendData={dataGetterForType('calls_scored')}
                helpText={() =>
                  `The total number of scorecards that ${listener} completed on rep's calls during the ${periodText} period of the report.`
                }
              />
            </div>
          </section>
        )}
      </section>
    ) : null;
  }
}
